import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiLogoYoutube } from "react-icons/bi";

import { useGetInterfaceMessageQuery } from "../../app/services/chatbot-rtk.services";

type AlertProps = {
  image_link?: string;
  video_link?: string;
  message_date_time: string; // ISO string or Date
  message?: string;
  cta: string;
  cta_target?: string;
  level?: "info" | "warning" | "error";
};

const DashboardAlert = () => {
  const {
    data: alertData,
    isLoading,
    isError,
    isSuccess,
    refetch
  } = useGetInterfaceMessageQuery();
  const { i18n } = useTranslation();
  const [index, setIndex] = useState(0);
  const [currentAlert, setCurrentAlert] = useState<AlertProps | null>(null);

  useEffect(() => {
    if (i18n.language) {
      if (!alertData || Object.keys(alertData).length <= 0) return;
      const interval = setInterval(() => {
        setCurrentAlert(
          (alertData as any)[i18n.language.startsWith("en") ? "en" : "fr"][
            index
          ]
        );
        setIndex(
          (prevIndex) =>
            (prevIndex + 1) %
            (alertData as any)[i18n.language.startsWith("en") ? "en" : "fr"]
              .length
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [alertData, index, i18n.language]);

  useEffect(() => {
    if (!i18n.language) return;

    if (isSuccess) {
      if (
        !alertData ||
        Object.keys(alertData).length <= 0 ||
        !alertData[i18n.language.startsWith("en") ? "en" : "fr"] ||
        alertData[i18n.language.startsWith("en") ? "en" : "fr"].length <= 0
      )
        return;
      setCurrentAlert(
        (alertData as any)[i18n.language.startsWith("en") ? "en" : "fr"][0]
      );
      setIndex(0);
    }
  }, [alertData, i18n.language, isSuccess]);

  let content = null;

  if (isLoading) {
    content = <div>Loading...</div>;
  }

  if (isError) {
    content = (
      <div>
        Error happened... <button onClick={refetch}>Retry</button>{" "}
      </div>
    );
  }

  if (isSuccess && currentAlert) {
    content = (
      <React.Fragment>
        {/* Media Section - Hidden on Small Screens */}
        <div className="tw-flex-[1] lg:tw-block tw-hidden">
          {renderMedia(currentAlert)}
        </div>

        {/* Message Section - Adjust Text Size Responsively */}
        <div className="tw-flex-[2] tw-text-gray-800 tw-text-center tw-font-medium tw-font-['Lexend'] tw-leading-normal lg:tw-text-base tw-text-sm">
          {currentAlert.message}
        </div>

        <div className="tw-flex-[1]">
          <div className="tw-flex tw-items-center tw-justify-end">
            {currentAlert.cta && (
              <a
                href={currentAlert.cta_target ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className={`tw-px-4 tw-py-2 ${
                  currentAlert.level === "info"
                    ? "tw-bg-blue-700"
                    : currentAlert.level === "warning"
                    ? "tw-bg-yellow-700"
                    : "tw-bg-red-700"
                } tw-rounded-full tw-shadow-sm tw-flex tw-justify-center tw-items-center tw-overflow-hidden`}
              >
                <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {currentAlert.cta}
                </div>
              </a>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }

  if (!currentAlert) return null;

  return (
    <div
      style={{
        boxShadow: "0 -4px 8px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.2)"
      }}
      className="tw-min-h-[90px] tw-w-full tw-relative tw-pl-4 tw-pr-3 tw-py-3 tw-bg-white tw-shadow-xl tw-flex  tw-items-center lg:tw-flex-row tw-flex-col lg:tw-gap-0 tw-gap-2 tw-mt-2"
    >
      {content}
    </div>
  );
};

const renderMedia = (item: AlertProps) => {
  if (item.image_link) {
    return (
      <a
        href={item.cta_target ?? "#"}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img className="tw-h-[50px]" src={item.image_link} alt="Alert" />
      </a>
    );
  }

  if (item.video_link) {
    return (
      <a href={item.video_link} target="_blank" rel="noopener noreferrer">
        <BiLogoYoutube size={50} className="tw-text-red-500" />
      </a>
    );
  }

  return null;
};

export default DashboardAlert;
